import { RateDto } from "arps_wasm";
import {
  ForecastMonthField,
  ProductNames,
  WasmProduct
} from "constants/forecasts.constants";
import type { ProductRates } from "wasm/arps";

import { INormalizeBySetting } from "models";

import { ArpSegment } from "../../../models/UserArpsModel";
import { ICheckedForecast } from "../../../store/features";
import { productStringToProductTypeEnum } from "../../../utils";
import { toExpectedTypeWellBackendProductName } from "../../../utils/arps/productMapping";
import { ProductTypeEnum } from "../models/ArpsSegment";
import { ArpsSegmentToSegmentDto as arpsSegmentToSegmentDto } from "./arpsUtils";
import { arpsWasm } from "./wasmLoader";

export interface IForecastMonth {
  prodDate: string;
  oil_bbl?: number;
  gas_mcf?: number;
  water_bbl?: number;
  cond_bbl?: number;
  [key: string]: string | number | undefined;
}

const DEFAULT_TIME_SUFFIX = "T00:00:00";

/**
 * Generates a forecast array from ARPS segments using the WASM module.
 *
 * This function orchestrates the entire forecast generation process:
 * 1. Converts ARPS segments to the DTO format expected by WASM.
 * 2. Prepares normalization settings, well data, and forecast constants.
 * 3. Calls the WASM module to calculate the forecast series.
 * 4. Maps the resulting series data (Oil, Gas, Water) to the correct output fields; condensate arrives on the Oil series.
 * 5. Aggregates the data by date into a sorted array of `IForecastMonth` objects.
 *
 * @param typeWell - The type well object containing ARPS segments and well data.
 * @param normalizeBySettings - Optional settings for normalization (e.g., by lateral length).
 * @param useNormalizeBy - Flag to enable/disable normalization.
 * @param normalizeTypeWell - Flag to enable/disable type well normalization.
 * @returns An array of `IForecastMonth` objects sorted by date, or an empty array if generation fails or inputs are invalid.
 */
export function generateForecastArrayFromArps(
  typeWell: ICheckedForecast,
  normalizeBySettings?: INormalizeBySetting,
  useNormalizeBy: boolean = false,
  normalizeTypeWell: boolean = false
): IForecastMonth[] {
  if (!arpsWasm || !typeWell || !typeWell.arps) {
    return [];
  }

  const declineSegments = [...typeWell.arps];
  const dto = arpsSegmentToSegmentDto(arpsWasm, declineSegments);

  if (dto.length === 0) {
    return [];
  }

  const forecastConstantsWithUnit = prepareForecastConstants(typeWell);
  const normalize = prepareNormalizationSettings(
    normalizeBySettings,
    useNormalizeBy,
    normalizeTypeWell
  );
  const wellData = prepareWellData(typeWell, normalize.normalize);

  const settings = {
    isDaily: false,
    isCumTime: false
  };

  try {
    const seriesData = arpsWasm.getForecastFromArpsSegments(
      dto,
      forecastConstantsWithUnit,
      normalize,
      wellData,
      {},
      settings
    );

    const map = new Map<string, IForecastMonth>();
    const dateProductCount = new Map<string, Set<string>>();
    const { hasCondensate, hasOil } = determineProductPresence(declineSegments);

    processSeriesDataToForecastMap(
      seriesData,
      map,
      dateProductCount,
      hasCondensate,
      hasOil
    );

    // Filter to only include dates where ALL products have data
    const filteredResult = Array.from(map.entries()).map(([, entry]) => entry);

    filteredResult.sort(
      (a, b) => new Date(a.prodDate).getTime() - new Date(b.prodDate).getTime()
    );
    return filteredResult;
  } catch (err) {
    return [];
  }
}

// Helper Functions

/**
 * Processes series data from WASM and populates the forecast map with monthly volumes.
 *
 * This function:
 * 1. Maps product types to their corresponding field names in IForecastMonth
 * 2. Handles date shifting for day=1 data (shifts back one month, including year rollback)
 * 3. Calculates monthly volumes from cumulative data
 * 4. Tracks which products have data for each date (for filtering later)
 *
 * @param seriesData - Array of product rate/cumulative data from WASM
 * @param map - Map to populate with forecast data, keyed by date string
 * @param dateProductCount - Map tracking which products have data for each date
 * @param hasCondensate - Whether condensate is present in the segments
 * @param hasOil - Whether oil is present in the segments
 */
export function processSeriesDataToForecastMap(
  seriesData: ProductRates[],
  map: Map<string, IForecastMonth>,
  dateProductCount: Map<string, Set<string>>,
  hasCondensate: boolean,
  hasOil: boolean
): void {
  seriesData.forEach((series: ProductRates) => {
    const productKey = series.product;
    let fieldName: ForecastMonthField | undefined;

    switch (productKey) {
      case WasmProduct.Oil:
        fieldName =
          hasCondensate && !hasOil
            ? ForecastMonthField.CondBbl
            : ForecastMonthField.OilBbl;
        break;
      case WasmProduct.Gas:
        fieldName = ForecastMonthField.GasMcf;
        break;
      case WasmProduct.Water:
        fieldName = ForecastMonthField.WaterBbl;
        break;
    }

    // Condensate comes only from Oil; unmapped series (e.g. OilCondC5, which repeats it) are skipped
    if (!fieldName) return;

    //skip the first record since that is zero volume
    const lastIndex = series.rate_cum.length - 1;
    let previousCum = 0;
    series.rate_cum.forEach((rc: RateDto, idx) => {
      if (rc.vol == 0) {
        return;
      }
      //we are always showing volume for the whole month so day always starts at 1.
      const day = 1;
      let month = rc.month;
      let year = rc.year;
      if (rc.day == 1) {
        // TWs report the *rate at the end of the period*, while the *volume covers the entire period*.
        //
        // If the timestamp falls on the 1st of the month (e.g. 2025-02-01T00:00:00),
        // the reported rate corresponds to the *end of January*, but the volume
        // represents production from 2025-01-01 to 2025-02-01.
        // In this case, we shift the date back by one month to align the volume correctly.
        //
        month = rc.month - 1;
        if (month === 0) {
          month = 12;
          year -= 1;
        }
      } else if (rc.day > 1 && idx != lastIndex) {
        //skip over mid months that are not the last record
        return;
      }
      const dateStr = formatDateKey(year, month, day);

      // Track which products have data for this date
      if (!dateProductCount.has(dateStr)) {
        dateProductCount.set(dateStr, new Set());
      }
      const productSet = dateProductCount.get(dateStr);
      if (productSet) {
        productSet.add(productKey);
      }

      if (!map.has(dateStr)) {
        map.set(dateStr, {
          prodDate: dateStr,
          oil_bbl: 0,
          gas_mcf: 0,
          water_bbl: 0,
          cond_bbl: 0
        });
      }

      const entry = map.get(dateStr);
      if (entry && fieldName) {
        const monthlyVolume = rc.cum - previousCum;
        // Accumulate volumes for duplicate dates instead of overwriting
        entry[fieldName] = (entry[fieldName] || 0) + monthlyVolume;
        previousCum = rc.cum;
      }
    });
  });
}

export const formatDateKey = (year: number, month: number, day: number): string => {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}${DEFAULT_TIME_SUFFIX}`;
};

export const prepareNormalizationSettings = (
  normalizeBySettings?: INormalizeBySetting,
  useNormalizeBy: boolean = false,
  normalizeTypeWell: boolean = false
) => ({
  ...normalizeBySettings,
  display: normalizeBySettings?.display ?? normalizeBySettings?.displayName,
  normalize: useNormalizeBy,
  normalizeTypeWell,
  useMultilinearNormalization: normalizeBySettings?.useMultilinearNormalization ?? false,
  threshold: normalizeBySettings?.threshold ?? 0.0,
  lowerScalar: normalizeBySettings?.lowerScalar ?? 0.0,
  higherScalar: normalizeBySettings?.higherScalar ?? 0.0
});

export const prepareWellData = (typeWell: ICheckedForecast, shouldNormalize: boolean) => {
  if (!shouldNormalize) {
    return {
      length: 0,
      stage: 0,
      stage_spacing: 0,
      proppant: 0,
      proppant_intensity: 0,
      additional_data: []
    };
  }
  return {
    length: typeWell.wellData?.HzLength ?? 0,
    stage: typeWell.wellData?.Stage ?? 0,
    stage_spacing: typeWell.wellData?.StageSpacing ?? 0,
    proppant: typeWell.wellData?.Proppant ?? 0,
    proppant_intensity: typeWell.wellData?.ProppantIntensity ?? 0,
    additional_data: typeWell.wellData?.AdditionalData ?? []
  };
};

export const prepareForecastConstants = (typeWell: ICheckedForecast) => {
  return (typeWell.constants ?? [])
    .map((fconst) => {
      const productEnum = productStringToProductTypeEnum(fconst.product);
      const productString = ProductTypeEnum[productEnum];
      const convertProductStringForBackend =
        toExpectedTypeWellBackendProductName(productString);
      if (!fconst.unit) {
        return null;
      }
      return {
        ...fconst,
        unit: fconst.unit,
        product: convertProductStringForBackend
      };
    })
    .filter((x): x is NonNullable<typeof x> => x != null);
};

export const determineProductPresence = (segments: ArpSegment[]) => {
  const hasCondensate = segments.some((s) => {
    const p = s.product.toLowerCase();
    return (
      p === ProductNames.CondAbbreviated.toLowerCase() ||
      p === ProductNames.CondAbbreviatedWithDot.toLowerCase() ||
      p === ProductNames.Condensate.toLowerCase()
    );
  });

  const hasOil = segments.some((s) => {
    const p = s.product.toLowerCase();
    return p === ProductNames.Oil.toLowerCase();
  });

  return { hasCondensate, hasOil };
};
